'use client';

import React, { useEffect, useRef } from 'react';

interface Skill {
  name: string;
  category: string;
  icon: string;
  description: string;
}

const digitalSkills: Skill[] = [
  {
    name: 'SEO / GEO Optimization',
    category: 'Organic Growth',
    icon: '📈',
    description: 'Technical SEO, keyword strategy, on-page optimization, and AI-driven search visibility.',
  },
  {
    name: 'CMS Platforms',
    category: 'Technology',
    icon: '⚙️',
    description: 'WordPress, Webflow, Shopify, Drupal — building and managing digital experiences.',
  },
  {
    name: 'Analytics & Reporting',
    category: 'Data',
    icon: '📊',
    description: 'Google Analytics 4, data interpretation, KPI tracking, and performance reporting.',
  },
  {
    name: 'CRM & Automation',
    category: 'Tools',
    icon: '🔄',
    description: 'HubSpot CRM, Zapier workflows, email marketing automation, and lead nurturing.',
  },
];

const communicationsSkills: Skill[] = [
  {
    name: 'Content Strategy & Storytelling',
    category: 'Content',
    icon: '✍️',
    description: 'Crafting narratives that resonate — from editorial calendars to brand voice guidelines.',
  },
  {
    name: 'Social Media & Campaigns',
    category: 'Social',
    icon: '📱',
    description: 'Multi-platform campaign management, community building, and paid social strategy.',
  },
  {
    name: 'Design & Visual',
    category: 'Creative',
    icon: '🎨',
    description: 'Canva, Figma, Adobe Photoshop — creating visuals that support brand identity.',
  },
  {
    name: 'Communication Strategy',
    category: 'Strategy',
    icon: '💡',
    description: 'Brand positioning, messaging frameworks, stakeholder communication, and PR.',
  },
];

const cardBgColors: Record<string, string> = {
  'SEO / GEO Optimization': '#7c3aed',
  'Social Media & Campaigns': '#0ECAD4',
  'Analytics & Reporting': '#0ECAD4',
  'Design & Visual': '#7c3aed',
};

function SkillCard({ skill, index }: { skill: Skill; index: number }) {
  const bgColor = cardBgColors[skill.name];
  const isColored = !!bgColor;
  return (
    <div
      className="skill-card animate-on-scroll"
      style={{
        animationDelay: `${index * 80}ms`,
        backgroundColor: bgColor || '#ffffff',
      }}
    >
      <div className="text-3xl mb-4">{skill.icon}</div>
      <div
        className="section-label mb-2"
        style={{ fontSize: '0.65rem', color: isColored ? 'rgba(255,255,255,0.85)' : undefined }}
      >
        {skill.category}
      </div>
      <h3
        className="font-condensed font-bold uppercase mb-3"
        style={{ fontSize: '1.1rem', lineHeight: '1.2', color: isColored ? '#ffffff' : undefined }}
      >
        {skill.name}
      </h3>
      <p
        className="font-sans"
        style={{
          fontSize: '0.85rem',
          lineHeight: '1.6',
          color: isColored ? 'rgba(255,255,255,0.85)' : undefined,
          opacity: isColored ? 1 : 0.6,
        }}
      >
        {skill.description}
      </p>
    </div>
  );
}

export default function Skills() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.animate-on-scroll').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 80);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="skills" className="bg-white py-24 md:py-32" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-5 md:px-16 lg:px-24">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <span className="section-label animate-on-scroll">02 — Skills & Tools</span>
            <h2
              className="section-title mt-4 animate-on-scroll"
              style={{ fontSize: 'clamp(2.5rem, 5vw, 5rem)' }}
            >
              My
              <br />
              <span className="text-primary">Toolkit</span>
            </h2>
          </div>
          <p
            className="font-sans text-dark opacity-70 max-w-sm animate-on-scroll"
            style={{ fontSize: '0.95rem', lineHeight: '1.7' }}
          >
            A versatile set of skills built across agencies, NGOs, and brand environments.
            Always learning, always adapting.
          </p>
        </div>

        {/* Two Groups Side by Side */}
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Digital Group */}
          <div className="flex-1 min-w-0">
            <div className="mb-4 animate-on-scroll">
              <span className="font-condensed font-bold text-sm tracking-widest uppercase text-dark opacity-60">
                💻 Digital
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-secondary-dark">
              {digitalSkills.map((skill, index) => (
                <SkillCard key={skill.name} skill={skill} index={index} />
              ))}
            </div>
          </div>

          {/* Communications Group */}
          <div className="flex-1 min-w-0">
            <div className="mb-4 animate-on-scroll">
              <span className="font-condensed font-bold text-sm tracking-widest uppercase text-dark opacity-60">
                📢 Communications
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-secondary-dark">
              {communicationsSkills.map((skill, index) => (
                <SkillCard key={skill.name} skill={skill} index={index + 4} />
              ))}
            </div>
          </div>
        </div>

        {/* Tool logos row */}
        <div className="mt-16 pt-8 border-t border-secondary-dark animate-on-scroll">
          <p className="font-condensed text-xs tracking-widest uppercase text-dark opacity-40 mb-6">
            Tools I work with daily
          </p>
          <div className="flex flex-wrap gap-4">
            {[
              { label: 'WordPress', bg: '#7c3aed', text: '#ffffff', border: '#7c3aed' },
              { label: 'Webflow', bg: '#0ECAD4', text: '#ffffff', border: '#0ECAD4' },
              { label: 'Shopify', bg: '#ffffff', text: '#7c3aed', border: '#7c3aed' },
              { label: 'Drupal', bg: '#7c3aed', text: '#ffffff', border: '#7c3aed' },
              { label: 'Google Analytics', bg: '#0ECAD4', text: '#ffffff', border: '#0ECAD4' },
              { label: 'HubSpot', bg: '#ffffff', text: '#0ECAD4', border: '#0ECAD4' },
              { label: 'Canva', bg: '#7c3aed', text: '#ffffff', border: '#7c3aed' },
              { label: 'Figma', bg: '#0ECAD4', text: '#ffffff', border: '#0ECAD4' },
              { label: 'Photoshop', bg: '#ffffff', text: '#7c3aed', border: '#7c3aed' },
              { label: 'Zapier', bg: '#7c3aed', text: '#ffffff', border: '#7c3aed' },
              { label: 'Meta Ads', bg: '#0ECAD4', text: '#ffffff', border: '#0ECAD4' },
              { label: 'Google Ads', bg: '#ffffff', text: '#0ECAD4', border: '#0ECAD4' },
            ].map((tool) => (
              <span
                key={tool.label}
                className="font-condensed font-bold text-xs tracking-wider uppercase px-4 py-2 cursor-default transition-all duration-300"
                style={{ backgroundColor: tool.bg, color: tool.text, border: `1.5px solid ${tool.border}` }}
              >
                {tool.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}